/**
 * W3 casing model — pure logic for the casing / tubular design screen. No React,
 * no I/O: computed length, the concentric nesting validation, and the string-type
 * → colour / label mapping the grid and schematic share. Unit-tested standalone.
 *
 * Nesting rule (from w3-casing-design.html, §2–4): a casing telescope must taper
 * inward from the outermost (Conductor, shallowest) to the innermost (Production,
 * deepest). For each string, in row order:
 *   casing OD  <  hole size of its own section  <  casing OD of the string above.
 * A string whose OD is ≥ its own hole (can't fit the hole it's run in) or ≥ the
 * previous string's OD (doesn't nest inside the prior casing) is flagged.
 */
import type { CasingRow } from "@api/types";

/** The four canonical string types, outermost → innermost. */
export const CASING_TYPES = ["conductor", "surface", "intermediate", "production"] as const;
export type CasingType = (typeof CASING_TYPES)[number];

/** Normalize the free-text `type` to a canonical key (case / accent tolerant). */
export function normalizeType(type: string | null | undefined): CasingType | null {
  const k = (type ?? "").toLowerCase().trim();
  if (k.startsWith("conduct")) return "conductor";
  if (k.startsWith("surf") || k.startsWith("superf")) return "surface";
  if (k.startsWith("inter")) return "intermediate";
  if (k.startsWith("prod")) return "production";
  return null;
}

/** The `--op-*` / palette token that colours each canonical string type. */
export const CASING_TYPE_VAR: Record<CasingType, string> = {
  conductor: "--op-pad",
  surface: "--oxbo-z",
  intermediate: "--op-workover",
  production: "--good",
};

/** Colour token for a row (falls back to a neutral when the type is unknown). */
export function typeVar(type: string | null | undefined): string {
  const k = normalizeType(type);
  return k ? CASING_TYPE_VAR[k] : "--dim";
}

/** Base − Top installation depth, guarding nulls. */
export function computedLength(r: CasingRow): number | null {
  if (r.top_installation == null || r.base_installation_depth == null) return null;
  return r.base_installation_depth - r.top_installation;
}

/** Why a string failed nesting (used to key the inline flag / tooltip). */
export type NestingCode = "od_ge_hole" | "od_ge_prev" | "base_le_prev";

export interface NestingResult {
  /** Row index → its violations (empty array = OK). Parallel to the input rows. */
  byRow: NestingCode[][];
  /** Total count of rows carrying at least one violation. */
  warnCount: number;
}

/**
 * Validate the concentric nesting of a casing string list in row order.
 * Row order is authoritative (Conductor first → Production last); missing values
 * never raise a violation (an incomplete draft is not "wrong", just unfinished).
 */
export function validateNesting(rows: CasingRow[]): NestingResult {
  const byRow: NestingCode[][] = [];
  let warnCount = 0;
  let prevOd: number | null = null;
  let prevBase: number | null = null;

  for (const r of rows) {
    const flags: NestingCode[] = [];
    const od = r.casing_size;
    const hole = r.hole_size;
    const base = r.base_installation_depth;

    // OD must fit inside the hole drilled for its own section.
    if (od != null && hole != null && od >= hole) flags.push("od_ge_hole");
    // OD must be smaller than the string it runs inside (the one above).
    if (od != null && prevOd != null && od >= prevOd) flags.push("od_ge_prev");
    // Each string should reach deeper than the one above it.
    if (base != null && prevBase != null && base <= prevBase) flags.push("base_le_prev");

    if (flags.length > 0) warnCount += 1;
    byRow.push(flags);
    // Advance the "previous string" cursor only on rows that carry the value.
    if (od != null) prevOd = od;
    if (base != null) prevBase = base;
  }

  return { byRow, warnCount };
}

/** Roll-up KPIs for the header chips. */
export interface CasingTotals {
  count: number;
  /** Deepest casing shoe (max base installation depth). */
  deepestShoe: number;
  /** OD of the innermost production string, if any (for the KPI chip). */
  productionOd: number | null;
  warnCount: number;
}

export function casingTotals(rows: CasingRow[], nesting: NestingResult): CasingTotals {
  let deepestShoe = 0;
  let productionOd: number | null = null;
  for (const r of rows) {
    if ((r.base_installation_depth ?? 0) > deepestShoe) deepestShoe = r.base_installation_depth ?? 0;
    if (normalizeType(r.type) === "production" && r.casing_size != null) productionOd = r.casing_size;
  }
  return { count: rows.length, deepestShoe, productionOd, warnCount: nesting.warnCount };
}

/** Next free seq for a new string (max + 1, 1-based). */
export function nextSeq(rows: CasingRow[]): number {
  return rows.reduce((m, r) => Math.max(m, r.seq), 0) + 1;
}
